import { DOCUMENT } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  type ElementRef,
  inject,
  input,
  type OnDestroy,
  output,
  viewChild,
} from '@angular/core';
import type { GuidedTourStep } from '../guided-tour.model';
import { GuidedTourStepComponent } from '../guided-tour-step/guided-tour-step';
import { computeTourLayout, type TourBox } from './guided-tour-layout';

const MOVE_DURATION_MS = 400;

/**
 * Dims and blocks everything but the step's element, which it frames, and places the step next
 * to it. It follows the element on every frame, so it keeps up when the dialog scrolls.
 */
@Component({
  selector: 'nr-guided-tour-layer',
  imports: [GuidedTourStepComponent],
  templateUrl: './guided-tour-layer.html',
  styleUrl: './guided-tour-layer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuidedTourLayerComponent implements OnDestroy {
  private readonly window = inject(DOCUMENT).defaultView;
  private readonly layer = viewChild.required<ElementRef<HTMLElement>>('layer');
  private readonly spotlight = viewChild.required<ElementRef<HTMLElement>>('spotlight');
  private readonly bubble = viewChild.required<ElementRef<HTMLElement>>('bubble');
  private frameId: number | undefined;
  private moveTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private placedTarget: HTMLElement | undefined;
  private placedLayout = '';

  readonly target = input.required<HTMLElement>();

  readonly step = input.required<GuidedTourStep>();

  readonly stepNumber = input.required<number>();

  readonly stepCount = input.required<number>();

  readonly next = output();

  readonly skip = output();

  readonly done = output();

  constructor() {
    afterNextRender(() => {
      this.followTarget();
    });
  }

  ngOnDestroy(): void {
    if (this.frameId !== undefined) this.window?.cancelAnimationFrame(this.frameId);
    clearTimeout(this.moveTimeoutId);
  }

  private followTarget(): void {
    this.place();
    this.frameId = this.window?.requestAnimationFrame(() => {
      this.followTarget();
    });
  }

  private place(): void {
    const target = this.target();
    if (this.placedTarget && this.placedTarget !== target) this.animateMove();
    this.placedTarget = target;

    const bubble = this.bubble().nativeElement;
    const layout = computeTourLayout({
      target: GuidedTourLayerComponent.toBox(target.getBoundingClientRect()),
      bubble: { width: bubble.offsetWidth, height: bubble.offsetHeight },
      viewport: { width: this.window?.innerWidth ?? 0, height: this.window?.innerHeight ?? 0 },
    });
    const serialized = JSON.stringify(layout);
    if (serialized === this.placedLayout) return;
    this.placedLayout = serialized;

    const { spotlight } = layout;
    Object.assign(this.spotlight().nativeElement.style, {
      width: `${spotlight.width}px`,
      height: `${spotlight.height}px`,
      transform: `translate(${spotlight.left}px, ${spotlight.top}px)`,
    });
    bubble.style.transform = `translate(${layout.bubble.left}px, ${layout.bubble.top}px)`;
  }

  private animateMove(): void {
    const layer = this.layer().nativeElement;
    layer.classList.add('moving');
    clearTimeout(this.moveTimeoutId);
    this.moveTimeoutId = setTimeout(() => {
      layer.classList.remove('moving');
    }, MOVE_DURATION_MS);
  }

  private static toBox({ top, left, width, height }: DOMRect): TourBox {
    return { top, left, width, height };
  }
}
